import React, { useState, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import type { ProcessingActivity } from '../../hooks/useWorkspace';

interface ProcessingActivityFeedProps {
  activities: ProcessingActivity[];
  startTime: number | null;
}

const procFadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const procItemIn = keyframes`
  from { opacity: 0; transform: translateX(-8px); }
  to   { opacity: 1; transform: translateX(0); }
`;

const procSpin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const procDotPulse = keyframes`
  0%, 100% { opacity: 1; box-shadow: 0 0 0 0 var(--accent, #6366f1); }
  50%      { opacity: 0.5; box-shadow: 0 0 0 4px var(--accent-subtle, rgba(99,102,241,0.15)); }
`;

const Card = styled.div`
  background: var(--bg-surface, #fff);
  border: 1px solid var(--border, #e5e7eb);
  border-radius: 12px;
  padding: 0.875rem 1rem;
  margin-left: 2.5rem;
  animation: ${procFadeIn} 0.3s ease;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.625rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-muted, #999);
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const HeaderIcon = styled.span`
  display: inline-block;
  animation: ${procSpin} 2s linear infinite;
`;

const Elapsed = styled.span`
  margin-left: auto;
  font-variant-numeric: tabular-nums;
  font-weight: 500;
  text-transform: none;
  letter-spacing: normal;
`;

const ItemsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`;

const Item = styled.div<{ $status: 'active' | 'completed' | 'error'; $delay: number }>`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.375rem 0.5rem;
  border-radius: 8px;
  font-size: 0.8125rem;
  transition: all 0.2s;
  animation: ${procItemIn} 0.3s ease both;
  animation-delay: ${({ $delay }) => $delay}s;

  ${({ $status }) =>
    $status === 'completed' &&
    css`
      opacity: 0.55;
    `}

  ${({ $status }) =>
    $status === 'active' &&
    css`
      background: var(--accent-subtle, rgba(99, 102, 241, 0.06));
      border: 1px solid var(--accent-muted, rgba(99, 102, 241, 0.15));
    `}

  ${({ $status }) =>
    $status === 'error' &&
    css`
      background: var(--error-subtle, #fef2f2);
      border: 1px solid #fecaca;
    `}
`;

const Dot = styled.span<{ $color: string; $pulse: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 0.3125rem;

  ${({ $color }) => {
    const colorMap: Record<string, string> = {
      teal: 'var(--accent, #6366f1)',
      blue: 'var(--info, #3b82f6)',
      orange: 'var(--warning, #f59e0b)',
      red: 'var(--error, #ef4444)',
      green: 'var(--success, #22c55e)',
    };
    return css`
      background: ${colorMap[$color] || colorMap.teal};
    `;
  }}

  ${({ $pulse }) =>
    $pulse &&
    css`
      animation: ${procDotPulse} 1.5s ease-in-out infinite;
    `}
`;

const Content = styled.div`
  flex: 1;
  min-width: 0;
`;

const Label = styled.div`
  font-weight: 600;
  color: var(--text-primary, #1a1a1a);
  font-size: 0.8125rem;
  line-height: 1.3;
`;

const Detail = styled.div`
  font-size: 0.75rem;
  color: var(--text-muted, #999);
  line-height: 1.4;
`;

const Check = styled.span`
  color: var(--success, #22c55e);
  font-size: 0.8125rem;
  flex-shrink: 0;
  margin-top: 0.125rem;
`;

function formatElapsed(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remaining = seconds % 60;
  return `${minutes}m ${remaining}s`;
}

const ProcessingActivityFeed: React.FC<ProcessingActivityFeedProps> = ({
  activities,
  startTime,
}) => {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!startTime) {
      setElapsed(0);
      return;
    }
    setElapsed(Date.now() - startTime);
    const timer = setInterval(() => {
      setElapsed(Date.now() - startTime);
    }, 1000);
    return () => clearInterval(timer);
  }, [startTime]);

  if (activities.length === 0) return null;

  return (
    <Card>
      <Header>
        <HeaderIcon>&#x2699;</HeaderIcon>
        <span>Processing</span>
        <Elapsed>{formatElapsed(elapsed)}</Elapsed>
      </Header>
      <ItemsList>
        {activities.map((a, i) => (
          <Item key={a.id} $status={a.status} $delay={i * 0.05}>
            <Dot $color={a.dotColor} $pulse={a.status === 'active' && a.dotColor === 'teal'} />
            <Content>
              <Label>{a.label}</Label>
              {a.detail && <Detail>{a.detail}</Detail>}
            </Content>
            {a.status === 'completed' && <Check>&#x2713;</Check>}
          </Item>
        ))}
      </ItemsList>
    </Card>
  );
};

export default ProcessingActivityFeed;
